/**
 * Nano Banana Pro — Custom MCP Server
 *
 * Provides generate_image and edit_image tools with built-in prompt optimization.
 * Images are saved to the session's data directory.
 */

import { createSdkMcpServer, tool } from '@anthropic-ai/claude-agent-sdk';
import { z } from 'zod';
import { optimizeImagePrompt, generateImage, editImage, GeminiApiError, type GeminiImageConfig } from './gemini-client.ts';
import { debug } from '../utils/debug.ts';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ASPECT_RATIOS = ['1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'] as const;
const RESOLUTIONS = ['1K', '2K', '4K'] as const;

const STYLE_PRESET_KEYS = ['sketchnote', 'hand-drawn-flowchart', 'blueprint', 'bento-grid', 'engineering-view'] as const;
type StylePresetKey = typeof STYLE_PRESET_KEYS[number];

const STYLE_PRESETS: Record<StylePresetKey, { label: string; style: string; mood: string; typography: string }> = {
  'sketchnote': {
    label: 'Sketchnote Doodle',
    style: 'Hand-drawn sketchnote doodle style with marker-like strokes, informal lettering, visual metaphors, arrows and connectors, whiteboard aesthetic. Think visual note-taking meets infographic.',
    mood: 'Playful, energetic, educational. Warm whites and blacks with accent color pops.',
    typography: 'Hand-drawn lettering, informal block letters for headings, neat handwriting for body text',
  },
  'hand-drawn-flowchart': {
    label: 'Hand-Drawn Flowchart',
    style: 'Hand-drawn flowchart/diagram style with rough sketch lines, imperfect boxes and arrows, pencil or felt-tip pen aesthetic. Technical but approachable.',
    mood: 'Clean, structured yet organic. Light background with dark ink strokes and subtle color coding.',
    typography: 'Neat hand-printed text in diagram boxes, monospace-style labels',
  },
  'blueprint': {
    label: 'Blueprint Infographic',
    style: 'Technical blueprint infographic style with grid lines, engineering notation, cross-section views, precise callout lines, measurement annotations. Deep blue background with white/cyan linework.',
    mood: 'Technical, precise, authoritative. Deep navy/blue (#2b336e) background with white and cyan (#00b3dd) line art.',
    typography: 'Monospace font for labels, serif font for titles, all-caps for section headers',
  },
  'bento-grid': {
    label: 'Apple Keynote Bento Grid',
    style: 'Apple-style Bento grid layout with clean rounded-corner cards arranged in an asymmetric grid. Each card contains a single concept, icon, or metric. Minimal, premium, high whitespace.',
    mood: 'Sophisticated, minimal, premium. Dark mode (#121212 background) with subtle card surfaces and accent colors.',
    typography: 'Clean sans-serif (similar to SF Pro/Haffer XH), tight letter-spacing, large bold numbers for metrics',
  },
  'engineering-view': {
    label: 'Exquisite Engineering View',
    style: 'Exquisite engineering cutaway/exploded view style showing internal structure, layered components, precise annotations, isometric or section views. Technical illustration with artistic beauty.',
    mood: 'Refined, intellectual, premium. Dark background with luminous detail rendering, subtle gradients.',
    typography: 'Serif font for titles (editorial elegance), monospace for technical annotations and callouts',
  },
};

const MIME_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
};

const SUPPORTED_EXTENSIONS = new Set(Object.keys(MIME_TYPES));

function inferMimeType(filePath: string): string | null {
  const ext = filePath.split('.').pop()?.toLowerCase();
  if (!ext) return null;
  return MIME_TYPES[ext] ?? null;
}

function fileExtFromMime(mimeType: string): string {
  if (mimeType.includes('jpeg')) return 'jpg';
  if (mimeType.includes('webp')) return 'webp';
  if (mimeType.includes('gif')) return 'gif';
  return 'png';
}

async function generateFilename(prefix: string, mimeType: string): Promise<string> {
  const { randomBytes } = await import('node:crypto');
  const hash = randomBytes(4).toString('hex');
  const ext = fileExtFromMime(mimeType);
  return `${prefix}-${Date.now()}-${hash}.${ext}`;
}

async function ensureDataDir(sessionPath: string): Promise<string> {
  const { join } = await import('node:path');
  const { mkdirSync, existsSync } = await import('node:fs');
  const dataDir = join(sessionPath, 'data');
  if (!existsSync(dataDir)) {
    mkdirSync(dataDir, { recursive: true });
  }
  return dataDir;
}

const DEFAULT_STYLE = 'Professional dark-mode infographic with vintage aesthetic touches. Clean, editorial layout.';
const DEFAULT_MOOD = 'Dark background (#121212), high-contrast text, warm accent colors. Vintage-modern blend.';
const DEFAULT_TYPOGRAPHY = 'Serif font (like Lyon Display) for titles and headings, clean sans-serif for body text';

function buildPrefsRecord(prefs?: {
  purpose?: string;
  style?: string;
  aspect_ratio?: string;
  resolution?: string;
  mood?: string;
  text_content?: string;
}): Record<string, string> {
  if (!prefs) {
    return {
      style: DEFAULT_STYLE,
      'mood/color palette': DEFAULT_MOOD,
      typography: DEFAULT_TYPOGRAPHY,
    };
  }
  const record: Record<string, string> = {};
  if (prefs.purpose) record.purpose = prefs.purpose;
  record.style = prefs.style || DEFAULT_STYLE;
  if (prefs.aspect_ratio) record['aspect ratio'] = prefs.aspect_ratio;
  if (prefs.resolution) record.resolution = prefs.resolution;
  record['mood/color palette'] = prefs.mood || DEFAULT_MOOD;
  if (!prefs.style && !prefs.mood) record.typography = DEFAULT_TYPOGRAPHY;
  if (prefs.text_content) record['text in image'] = prefs.text_content;
  return record;
}

function formatErrorMessage(error: unknown): string {
  if (error instanceof GeminiApiError) {
    if (error.isSafetyBlock) return error.message;
    if (error.isRateLimited) return error.message;
    return `Image generation failed: ${error.message}`;
  }
  return `Image generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
}

// ---------------------------------------------------------------------------
// Branding loader
// ---------------------------------------------------------------------------

async function loadWorkspaceBranding(workspacePath: string): Promise<string | undefined> {
  const { existsSync, readFileSync } = await import('node:fs');
  const { join } = await import('node:path');

  const sourcesDir = join(workspacePath, 'sources');
  if (!existsSync(sourcesDir)) return undefined;

  const brandingSlugs = ['branding-guideline', 'branding', 'brand-guideline', 'brand-guidelines', 'brand'];
  let brandingDir: string | undefined;

  for (const slug of brandingSlugs) {
    const candidate = join(sourcesDir, slug);
    if (existsSync(join(candidate, 'items'))) {
      brandingDir = candidate;
      break;
    }
  }

  if (!brandingDir) return undefined;

  const itemsDir = join(brandingDir, 'items');
  const priorityItems = ['color-palette', 'typography', 'visual-identity-logo', 'ui-patterns', 'brand-overview'];
  const sections: string[] = [];

  for (const itemSlug of priorityItems) {
    const contentPath = join(itemsDir, itemSlug, 'content.md');
    if (existsSync(contentPath)) {
      const content = readFileSync(contentPath, 'utf-8');
      const truncated = content.length > 800 ? content.substring(0, 800) + '\n[...truncated]' : content;
      sections.push(truncated);
    }
  }

  if (sections.length === 0) return undefined;

  debug(`[nano-banana] Loaded ${sections.length} branding sections from ${brandingDir}`);
  return sections.join('\n\n---\n\n');
}

// ---------------------------------------------------------------------------
// Server factory
// ---------------------------------------------------------------------------

export async function createNanoBananaServer(
  apiKey: string,
  sessionPath: string,
  workspacePath?: string,
): Promise<ReturnType<typeof createSdkMcpServer>> {
  debug(`[nano-banana] Creating server (session: ${sessionPath})`);

  const brandingContext = workspacePath ? await loadWorkspaceBranding(workspacePath) : undefined;
  if (brandingContext) {
    debug(`[nano-banana] Branding context loaded (${brandingContext.length} chars)`);
  }

  // -- generate_image tool --------------------------------------------------

  const generateImageTool = tool(
    'generate_image',
    `Generate a professional image using Nano Banana Pro (Google Gemini 3 Pro Image).

This tool creates high-quality images from text descriptions. It automatically optimizes your prompt using AI reasoning and applies workspace branding (colors, typography, style) by default.

## Built-in Style Presets

You can use a style_preset for instant professional results:
- **sketchnote** — Hand-drawn sketchnote doodle style (visual note-taking)
- **hand-drawn-flowchart** — Rough sketch flowcharts and diagrams
- **blueprint** — Technical blueprint infographic (deep blue with cyan linework)
- **bento-grid** — Apple Keynote-style Bento grid layout (dark, minimal, premium)
- **engineering-view** — Exquisite engineering cutaway/exploded views

## Default Behavior

When no style preset or explicit style preference is given, the system defaults to:
- Dark mode background (#121212)
- Vintage-modern infographic aesthetic
- Serif fonts for titles, sans-serif for body
- Brand colors from the workspace branding guideline (if available)

## When to Ask Clarifying Questions

Ask the user these questions ONLY if their request is vague or ambiguous:
1. What is the PURPOSE? (presentation, social media, document, etc.)
2. What STYLE? (or suggest a relevant preset from the list above)
3. What ASPECT RATIO? (default: 16:9 for presentations, 1:1 for social)
4. Any specific TEXT that should appear in the image?

For clear, specific requests, proceed directly with sensible defaults. Don't over-ask.`,
    {
      description: z.string().describe('What the user wants in the image — their core idea and intent'),
      style_preset: z.enum(STYLE_PRESET_KEYS).optional().describe(
        'Built-in style preset. When specified, overrides style/mood preferences with curated professional settings. Available: sketchnote, hand-drawn-flowchart, blueprint, bento-grid, engineering-view'
      ),
      preferences: z.object({
        purpose: z.string().optional().describe('Purpose: presentation, document, social media, icon, poster, etc.'),
        style: z.string().optional().describe('Style: photorealistic, illustration, diagram, infographic, minimalist, cinematic, etc.'),
        aspect_ratio: z.enum(ASPECT_RATIOS).optional().describe('Aspect ratio'),
        resolution: z.enum(RESOLUTIONS).optional().describe('Resolution (default: 2K)'),
        mood: z.string().optional().describe('Mood or color palette preferences'),
        text_content: z.string().optional().describe('Any text that should appear in the image'),
      }).optional().describe('User preferences gathered from the clarifying questions'),
    },
    async (args: {
      description: string;
      style_preset?: StylePresetKey;
      preferences?: {
        purpose?: string;
        style?: string;
        aspect_ratio?: typeof ASPECT_RATIOS[number];
        resolution?: typeof RESOLUTIONS[number];
        mood?: string;
        text_content?: string;
      };
    }) => {
      const { description, style_preset, preferences } = args;

      try {
        // 1. Apply style preset if specified (overrides style/mood)
        let effectivePrefs = preferences;
        if (style_preset && STYLE_PRESETS[style_preset]) {
          const preset = STYLE_PRESETS[style_preset];
          effectivePrefs = {
            ...preferences,
            style: preset.style,
            mood: preset.mood,
          };
        }

        // 2. Optimize the prompt via Gemini text model
        const prefsRecord = buildPrefsRecord(effectivePrefs);
        if (style_preset && STYLE_PRESETS[style_preset]) {
          prefsRecord.typography = STYLE_PRESETS[style_preset].typography;
        }
        debug(`[nano-banana] Optimizing prompt for: ${description.substring(0, 100)}`);
        const optimizedPrompt = await optimizeImagePrompt(description, prefsRecord, apiKey, brandingContext);

        // 3. Build image config
        const imageConfig: GeminiImageConfig = {};
        if (preferences?.aspect_ratio) imageConfig.aspectRatio = preferences.aspect_ratio;
        if (preferences?.resolution) imageConfig.imageSize = preferences.resolution;

        // 4. Generate the image
        debug('[nano-banana] Generating image...');
        const result = await generateImage(optimizedPrompt, apiKey, imageConfig);

        // 5. Save to disk
        const { join } = await import('node:path');
        const { writeFileSync } = await import('node:fs');
        const dataDir = await ensureDataDir(sessionPath);
        const filename = await generateFilename('nano-banana', result.mimeType);
        const filePath = join(dataDir, filename);
        writeFileSync(filePath, result.imageBuffer);

        debug(`[nano-banana] Image saved: ${filePath} (${result.imageBuffer.length} bytes)`);

        // 6. Return result
        const lines = [
          `✅ Image generated and saved successfully.`,
          ``,
          `**File:** ${filePath}`,
          `**Size:** ${(result.imageBuffer.length / 1024).toFixed(1)} KB`,
        ];
        if (style_preset && STYLE_PRESETS[style_preset]) {
          lines.push(`**Style preset:** ${STYLE_PRESETS[style_preset].label}`);
        }
        lines.push(
          ``,
          `**Optimized prompt used:**`,
          optimizedPrompt,
        );
        if (result.description) {
          lines.push('', `**Model notes:** ${result.description}`);
        }

        return { content: [{ type: 'text' as const, text: lines.join('\n') }] };
      } catch (error) {
        debug(`[nano-banana] generate_image error: ${error instanceof Error ? error.message : error}`);
        return {
          content: [{ type: 'text' as const, text: formatErrorMessage(error) }],
          isError: true,
        };
      }
    },
  );

  // -- edit_image tool ------------------------------------------------------

  const editImageTool = tool(
    'edit_image',
    `Edit or transform an existing image using Nano Banana Pro.

This tool modifies images based on instructions. You can change style, add elements, adjust lighting, translate text, change aspect ratio, and more. Style presets (sketchnote, blueprint, bento-grid, etc.) can be applied to transform the visual style.

Provide the path(s) to reference image(s) and describe the desired changes. Supports PNG, JPEG, WebP, and GIF formats.`,
    {
      instruction: z.string().describe('What changes to make to the image(s)'),
      image_paths: z.array(z.string()).min(1).max(14).describe('Absolute path(s) to reference image(s) to edit or use as references'),
      style_preset: z.enum(STYLE_PRESET_KEYS).optional().describe(
        'Built-in style preset to apply. Available: sketchnote, hand-drawn-flowchart, blueprint, bento-grid, engineering-view'
      ),
      preferences: z.object({
        aspect_ratio: z.enum(ASPECT_RATIOS).optional(),
        resolution: z.enum(RESOLUTIONS).optional(),
      }).optional().describe('Output image preferences'),
    },
    async (args: {
      instruction: string;
      image_paths: string[];
      style_preset?: StylePresetKey;
      preferences?: {
        aspect_ratio?: typeof ASPECT_RATIOS[number];
        resolution?: typeof RESOLUTIONS[number];
      };
    }) => {
      const { instruction, image_paths, style_preset, preferences } = args;

      try {
        const { existsSync, readFileSync } = await import('node:fs');
        const { join, extname } = await import('node:path');
        const { writeFileSync } = await import('node:fs');

        // 1. Read and validate reference images
        const referenceImages: Array<{ buffer: Buffer; mimeType: string }> = [];
        for (const imgPath of image_paths) {
          if (!existsSync(imgPath)) {
            return {
              content: [{ type: 'text' as const, text: `File not found: ${imgPath}` }],
              isError: true,
            };
          }
          const ext = extname(imgPath).slice(1).toLowerCase();
          if (!SUPPORTED_EXTENSIONS.has(ext)) {
            return {
              content: [{
                type: 'text' as const,
                text: `Unsupported image format: .${ext}. Supported formats: PNG, JPEG, WebP, GIF.`,
              }],
              isError: true,
            };
          }
          const mimeType = inferMimeType(imgPath)!;
          const buffer = readFileSync(imgPath);
          referenceImages.push({ buffer: Buffer.from(buffer), mimeType });
        }

        // 2. Build preferences with preset override
        const prefsRecord: Record<string, string> = { purpose: 'image editing' };
        if (style_preset && STYLE_PRESETS[style_preset]) {
          const preset = STYLE_PRESETS[style_preset];
          prefsRecord.style = preset.style;
          prefsRecord['mood/color palette'] = preset.mood;
          prefsRecord.typography = preset.typography;
        }
        if (preferences?.aspect_ratio) prefsRecord['aspect ratio'] = preferences.aspect_ratio;
        if (preferences?.resolution) prefsRecord.resolution = preferences.resolution;

        debug(`[nano-banana] Optimizing edit instruction: ${instruction.substring(0, 100)}`);
        const optimizedPrompt = await optimizeImagePrompt(instruction, prefsRecord, apiKey, brandingContext);

        // 3. Build image config
        const imageConfig: GeminiImageConfig = {};
        if (preferences?.aspect_ratio) imageConfig.aspectRatio = preferences.aspect_ratio;
        if (preferences?.resolution) imageConfig.imageSize = preferences.resolution;

        // 4. Edit the image
        debug(`[nano-banana] Editing image (${referenceImages.length} reference(s))...`);
        const result = await editImage(optimizedPrompt, referenceImages, apiKey, imageConfig);

        // 5. Save to disk
        const dataDir = await ensureDataDir(sessionPath);
        const filename = await generateFilename('nano-banana-edit', result.mimeType);
        const filePath = join(dataDir, filename);
        writeFileSync(filePath, result.imageBuffer);

        debug(`[nano-banana] Edited image saved: ${filePath} (${result.imageBuffer.length} bytes)`);

        // 6. Return result
        const lines = [
          `✅ Image edited and saved successfully.`,
          ``,
          `**File:** ${filePath}`,
          `**Size:** ${(result.imageBuffer.length / 1024).toFixed(1)} KB`,
        ];
        if (style_preset && STYLE_PRESETS[style_preset]) {
          lines.push(`**Style preset:** ${STYLE_PRESETS[style_preset].label}`);
        }
        lines.push(
          ``,
          `**Optimized instruction used:**`,
          optimizedPrompt,
        );
        if (result.description) {
          lines.push('', `**Model notes:** ${result.description}`);
        }

        return { content: [{ type: 'text' as const, text: lines.join('\n') }] };
      } catch (error) {
        debug(`[nano-banana] edit_image error: ${error instanceof Error ? error.message : error}`);
        return {
          content: [{ type: 'text' as const, text: formatErrorMessage(error) }],
          isError: true,
        };
      }
    },
  );

  // -- Assemble server ------------------------------------------------------

  return createSdkMcpServer({
    name: 'nano-banana-pro',
    version: '1.0.0',
    tools: [generateImageTool, editImageTool],
  });
}
